import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, CircularProgress, Box, Snackbar, Alert, 
  CssBaseline, ThemeProvider, createTheme, Button, Grid, Paper 
} from '@mui/material';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Dashboard from './Dashboard';
import TfPage from './TfPage'; 
import Footer from './components/Footer';
import GoogleLogo from './assets/g-logo.png';
import Logo from './gwmlogo.png';
import Header from './components/Header';
import GSCChatInterface from './components/GSCChatInterface'; // Import new component

import './App.css';

// Define a custom theme for consistency
const theme = createTheme({
  palette: {
    primary: {
      main: '#1a4d3a', // Dark green to match header
      light: '#2d5e4a',
      dark: '#0f2e1f',
    },
    secondary: {
      main: '#2563eb', // Professional blue for secondary actions
      light: '#3b82f6',
      dark: '#1d4ed8',
    },
    accent: {
      main: '#059669', // Vibrant green for accents
      light: '#10b981',
      dark: '#047857',
    },
    background: {
      default: '#f8fafc', // Slightly warmer background
      paper: '#ffffff',
      light: '#f1f5f9',
    },
    text: {
      primary: '#1a202c',
      secondary: '#4a5568',
      accent: '#1a4d3a', // Dark green for accent text
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: {
      fontFamily: "'Source Serif Pro', serif",
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#1a4d3a',
      letterSpacing: '-0.025em',
    },
    h2: {
      fontFamily: "'Source Serif Pro', serif",
      fontSize: '1.8rem',
      fontWeight: 600,
      color: '#1a4d3a',
      letterSpacing: '-0.025em',
    },
    h3: {
      fontFamily: "'Source Serif Pro', serif",
      fontSize: '1.4rem',
      fontWeight: 600,
      color: '#2d5e4a',
      letterSpacing: '-0.025em',
    },
    h4: {
      fontFamily: "'Source Serif Pro', serif",
      fontSize: '1.2rem',
      fontWeight: 600,
      color: '#2d5e4a',
      letterSpacing: '-0.025em',
    },
    body1: {
      color: '#2d3748',
      fontFamily: "'Inter', sans-serif",
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '8px 16px',
          fontFamily: "'Inter', sans-serif",
          fontWeight: 500,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          backgroundColor: '#1a4d3a',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#2d5e4a',
          },
        },
        outlined: {
          borderColor: '#1a4d3a',
          color: '#1a4d3a',
          '&:hover': {
            backgroundColor: '#f0f9f4',
            borderColor: '#2d5e4a',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#2d3748',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          '&:hover': {
            borderColor: '#2d5e4a',
          },
          '&.Mui-focused': {
            borderColor: '#1a4d3a',
            boxShadow: '0 0 0 3px rgba(26, 77, 58, 0.1)',
          },
        },
        select: {
          padding: '12px',
          fontFamily: "'Inter', sans-serif",
          color: '#2d3748',
        },
        icon: {
          color: '#4a5568',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          color: '#2d3748',
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f0f9f4',
          },
          '&.Mui-selected': {
            backgroundColor: '#e6f7ed',
            color: '#1a4d3a',
          },
        },
      },
    },
  },
});

const BACKEND = 'https://lobster-app-sd3s8.ondigitalocean.app';
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '487136404787-oi6buntg3ml1op9p4nk4dedjoj81h5i1.apps.googleusercontent.com';

// Privacy Policy Component
function PrivacyPolicy() {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h1" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Last Updated:</strong> June 05, 2025
      </Typography>

      <Typography variant="h2" gutterBottom>
        Introduction
      </Typography>
      <Typography variant="body1" paragraph>
        This Privacy Policy explains how Gasworks Media LLC ("we," "us," or "our") collects, uses, and protects your personal data when you use our SEO Research Tool ("Service") at gasworksmedia.com. Our address is 40 Main St, Pacific Beach, WA 98571. We are committed to complying with applicable data protection laws, including GDPR and CCPA.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Data We Collect
      </Typography>
      <Typography variant="body1" paragraph>
        We collect:
      </Typography>
      <ul>
        <li><strong>Personal Information:</strong> Name and email address during registration.</li>
        <li><strong>Usage Data:</strong> IP address and analytics data via Google Analytics.</li>
        <li><strong>Payment Information:</strong> Processed by Stripe for subscriptions.</li>
        <li><strong>Google Search Console Data:</strong> Access to your GSC data (e.g., clicks, impressions) if you authorize the GSC Chat feature.</li>
      </ul>

      <Typography variant="h2" gutterBottom>
        How We Collect Data
      </Typography>
      <Typography variant="body1" paragraph>
        - <strong>Directly:</strong> Through forms (e.g., registration, report creation).<br />
        - <strong>Automatically:</strong> Using cookies and third-party tracking tools like Google Analytics.<br />
        - <strong>Third Parties:</strong> Stripe collects payment data; SendGrid sends emails; Google APIs provide GSC data with your consent.<br />
      </Typography>

      <Typography variant="h2" gutterBottom>
        Purpose of Data Collection
      </Typography>
      <Typography variant="body1" paragraph>
        We use your data to:
      </Typography>
      <ul>
        <li>Provide the Service (e.g., analyze sitemaps, generate SEO insights).</li>
        <li>Process payments via Stripe.</li>
        <li>Send emails (e.g., weekly reports) via SendGrid.</li>
        <li>Analyze usage patterns with Google Analytics.</li>
        <li>Promote additional services (e.g., marketing emails).</li>
        <li>Enable the GSC Chat feature to query and analyze your Google Search Console data.</li>
      </ul>

      <Typography variant="h2" gutterBottom>
        Data Sharing
      </Typography>
      <Typography variant="body1" paragraph>
        We share data with:
      </Typography>
      <ul>
        <li><strong>Stripe:</strong> For payment processing (see Stripe's Privacy Policy at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>).</li>
        <li><strong>Google Analytics:</strong> For usage analysis (see Google's Privacy Policy at <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a>).</li>
        <li><strong>SendGrid:</strong> For email delivery (see SendGrid's Privacy Policy at <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer">https://www.twilio.com/legal/privacy</a>).</li>
        <li><strong>Google APIs:</strong> To access GSC data for the GSC Chat feature (see Google's Privacy Policy at <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a>).</li>
      </ul>
      <Typography variant="body1" paragraph>
        We do not sell your personal data.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Your Rights
      </Typography>
      <Typography variant="body1" paragraph>
        - <strong>Access, Update, Delete:</strong> Email us at service@gasworksmedia.com to manage your data.<br />
        - <strong>Opt-Out:</strong> Decline cookies via our cookie consent banner or unsubscribe from emails using the unsubscribe link.<br />
        - <strong>Consent:</strong> We obtain consent for cookies, email communications, and GSC data access per GDPR/CCPA.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Data Security
      </Typography>
      <Typography variant="body1" paragraph>
        We use encryption and secure protocols to protect your data. However, no system is 100% secure.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Contact Us
      </Typography>
      <Typography variant="body1" paragraph>
        For privacy inquiries, email us at service@gasworksmedia.com.
      </Typography>
    </Container>
  );
}

// Terms of Service Component
function TermsOfService() {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h1" gutterBottom>
        Terms of Service
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Last Updated:</strong> June 05, 2025
      </Typography>

      <Typography variant="h2" gutterBottom>
        Introduction
      </Typography>
      <Typography variant="body1" paragraph>
        These Terms of Service ("ToS") govern your use of the SEO Research Tool ("Service") provided by Gasworks Media LLC ("we," "us," or "our") at 40 Main St, Pacific Beach, WA 98571. By using the Service, you agree to these terms.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Use of Service
      </Typography>
      <Typography variant="body1" paragraph>
        - <strong>Eligibility:</strong> You must be 18+ to use the Service.<br />
        - <strong>Prohibited Actions:</strong> Do not misuse the Service (e.g., illegal activities, overloading servers).<br />
        - <strong>Accounts:</strong> You are responsible for your account security.<br />
        - <strong>Description:</strong> The Service analyzes sitemaps, provides SEO insights, and enables chatting with Google Search Console data via the GSC Chat feature.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Subscriptions and Payments
      </Typography>
      <Typography variant="body1" paragraph>
        - <strong>Pricing:</strong> Subscriptions start at $50/month and may change over time.<br />
        - <strong>Payments:</strong> Processed via Stripe (see Stripe's terms at <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">https://stripe.com/legal</a>).<br />
        - <strong>Cancellations:</strong> Cancel via your dashboard; no refunds after 14 days.<br />
        - <strong>Free Plan Limits:</strong> 1 report, 3 questions unless survey completed.<br />
        - <strong>Account Deletion:</strong> We may delete your account at our discretion, refunding any pre-paid subscription usage.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Intellectual Property
      </Typography>
      <Typography variant="body1" paragraph>
        All content and technology in the Service are owned by us. You may not copy or distribute it without permission.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Limitation of Liability
      </Typography>
      <Typography variant="body1" paragraph>
        We are not liable for indirect damages or data loss. The Service is provided "as is."
      </Typography>

      <Typography variant="h2" gutterBottom>
        Termination
      </Typography>
      <Typography variant="body1" paragraph>
        We may terminate your access for violating these ToS or at our discretion, with a refund for pre-paid usage.
      </Typography>

      <Typography variant="h2" gutterBottom>
        Contact Us
      </Typography>
      <Typography variant="body1" paragraph>
        For questions, email us at service@gasworksmedia.com.
      </Typography>
    </Container>
  );
}

// Success Page Component
function SuccessPage() {
  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom>
        Subscription Successful!
      </Typography>
      <Typography align="center">
        Thank you for subscribing. You can now enjoy unlimited access to the SEO Research Tool.
      </Typography>
      <Box display="flex" justifyContent="center" mt={2}>
        <Button variant="contained" color="primary" href="/dashboard">
          Go to Dashboard
        </Button>
      </Box>
    </Container>
  );
}

// Cancel Page Component
function CancelPage() {
  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom>
        Subscription Canceled
      </Typography>
      <Typography align="center">
        You have canceled your subscription. Return to the dashboard to continue with the free plan.
      </Typography>
      <Box display="flex" justifyContent="center" mt={2}>
        <Button variant="contained" color="primary" href="/dashboard">
          Go to Dashboard
        </Button>
      </Box>
    </Container>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('jwt') || '');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) {
      localStorage.setItem('jwt', t);
      setToken(t);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    console.log('Fetching /api/me with token:', token);
    fetch(`${BACKEND}/api/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json().then(data => ({ ok: res.ok, status: res.status, data })))
      .then(({ ok, status, data }) => {
        if (!ok) {
          if (status === 401 && data.error?.code === 'AuthenticationError') {
            setSnackbarMessage(data.error.message || 'Your session has expired. Please log in again.');
            setSnackbarOpen(true);
            localStorage.removeItem('jwt');
            setToken('');
          } else {
            throw new Error(data.error?.message || 'Failed to fetch user');
          }
        } else {
          console.log('User fetched:', data);
          setUser(data);
        }
      })
      .catch(err => {
        console.error('Error fetching user:', err.message);
        setSnackbarMessage(err.message || 'An error occurred while fetching user data.');
        setSnackbarOpen(true);
        localStorage.removeItem('jwt');
        setToken('');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      console.log('Google login credential:', credentialResponse);
      const res = await fetch(`${BACKEND}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Google login failed');
      }
      const { token } = data;
      console.log('Login response:', { token });
      localStorage.setItem('jwt', token);
      setToken(token);
    } catch (err) {
      console.error('Google login error:', err.message);
      setSnackbarMessage(err.message || 'Failed to authenticate with Google.');
      setSnackbarOpen(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('jwt');
    setToken('');
    setUser(null);
    setSnackbarOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Landing Page Content
  const renderLandingPage = () => (
    <div className="App landing-root">
      {/* Header */}
      <Box className="header-section">
        <img src={Logo} alt="GasWorks Media Logo" className="logo" />
        <Box>
          <Button
            variant="text"
            href="#features"
            className="nav-link"
          >
            Features
          </Button>
        </Box>
      </Box>

      {/* Hero Section */}
      <Box className="hero-section">
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Typography 
              variant="h1" 
              gutterBottom 
              className="hero-title"
              sx={{
                marginTop: '1em !important',
                marginBottom: '24px !important',
                marginLeft: 'auto !important',
                marginRight: 'auto !important',
                textAlign: 'center',
                color: '#ffffff !important',
                fontFamily: "'Source Serif Pro', serif",
                fontSize: '3.5rem',
                fontWeight: 700,
                lineHeight: 1.2,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}
            >
              SiteMind Research Beta
            </Typography>
            <Typography 
              variant="h2" 
              className="hero-subtitle"
              sx={{
                margin: '0 auto 32px !important',
                textAlign: 'center',
                color: '#ffffff !important',
                fontFamily: "'Inter', sans-serif",
                fontSize: '1.375rem',
                fontWeight: 400,
                lineHeight: 1.5,
                maxWidth: '700px'
              }}
            >
              Advanced SEO Research & Competitive Analysis Platform
            </Typography>
            <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => {
                  console.error('Google login failed');
                  setSnackbarMessage('Google login failed.');
                  setSnackbarOpen(true);
                }}
                useOneTap
                scope="profile email https://www.googleapis.com/auth/documents https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/webmasters"
                render={(renderProps) => (
                  <Button
                    variant="contained"
                    color="primary"
                    className="google-login-button"
                    onClick={renderProps.onClick}
                    disabled={renderProps.disabled}
                    startIcon={<img src={GoogleLogo} alt="Google logo" className="google-logo" />}
                    sx={{ py: 1.5, px: 4, fontSize: '1.1rem', minWidth: '200px' }}
                  >
                    Start Free Analysis
                  </Button>
                )}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }} className="features-section">
        <Typography variant="h2" align="center" gutterBottom className="section-title">
          Powerful Features for SEO Success
        </Typography>
        <Typography variant="body1" align="center" sx={{ 
          color: '#4a5568', 
          fontSize: '1.125rem', 
          maxWidth: '600px', 
          mx: 'auto', 
          mb: 6,
          lineHeight: 1.6
        }}>
          Everything you need to analyze, optimize, and dominate your competitive landscape
        </Typography>
        <Grid container spacing={4} id="features">
          <Grid item xs={12} sm={6} md={3}>
            <Paper className="feature-card">
              <Typography variant="h3" gutterBottom className="feature-title">
                AI-Powered Competitive Analysis
              </Typography>
              <Typography variant="body1" className="feature-text">
                Get deep insights into your digital footprint and see exactly how you stack up against competitors with advanced AI analysis.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className="feature-card">
              <Typography variant="h3" gutterBottom className="feature-title">
                Chat with Your Website Data
              </Typography>
              <Typography variant="body1" className="feature-text">
                Ask questions and get personalized optimization recommendations directly from your website's performance data.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className="feature-card">
              <Typography variant="h3" gutterBottom className="feature-title">
                Weekly Performance Monitoring
              </Typography>
              <Typography variant="body1" className="feature-text">
                Stay ahead with automated weekly reports comparing your digital presence to competitors and tracking progress.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className="feature-card">
              <Typography variant="h3" gutterBottom className="feature-title">
                Historical Performance Tracking
              </Typography>
              <Typography variant="body1" className="feature-text">
                Track changes over time with detailed snapshots of your SEO performance and competitor analysis trends.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Testimonials Section */}
      <Container maxWidth="lg" sx={{ py: 8 }} className="testimonials-section">
        <Typography variant="h2" align="center" gutterBottom className="section-title">
          Trusted by Industry Leaders
        </Typography>
        <Typography variant="body1" align="center" sx={{ 
          color: '#4a5568', 
          fontSize: '1.125rem', 
          maxWidth: '600px', 
          mx: 'auto', 
          mb: 6,
          lineHeight: 1.6
        }}>
          See how businesses are transforming their SEO strategy with SiteMind Research
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper className="testimonial-card">
              <Typography variant="body1" sx={{ fontStyle: 'italic' }} className="testimonial-text">
                "SiteMind Research completely transformed our SEO strategy. The AI insights helped us identify opportunities we never knew existed."
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }} className="testimonial-author">
                — Sarah Chen, VP Marketing at TechCorp
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper className="testimonial-card">
              <Typography variant="body1" sx={{ fontStyle: 'italic' }} className="testimonial-text">
                "The weekly monitoring keeps us ahead of competitors. We've seen a 40% increase in organic traffic since using this platform."
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }} className="testimonial-author">
                — Marcus Rodriguez, SEO Director at GrowthCo
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper className="testimonial-card">
              <Typography variant="body1" sx={{ fontStyle: 'italic' }} className="testimonial-text">
                "Chatting with our data is revolutionary. We get instant answers to complex SEO questions that used to take hours to research."
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }} className="testimonial-author">
                — Emily Watson, Digital Marketing Manager
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box className="cta-section">
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Typography variant="h2" gutterBottom className="section-title">
              Ready to Dominate Your Market?
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              fontSize: '1.25rem', 
              marginBottom: '2rem',
              maxWidth: '600px',
              margin: '0 auto 2rem',
              lineHeight: 1.6
            }}>
              Join thousands of businesses using SiteMind Research to gain competitive insights, 
              optimize their digital presence, and stay ahead of the competition.
            </Typography>
            <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" gap={2}>
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => {
                  console.error('Google login failed');
                  setSnackbarMessage('Google login failed.');
                  setSnackbarOpen(true);
                }}
                useOneTap
                scope="profile email https://www.googleapis.com/auth/documents https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/webmasters"
                render={(renderProps) => (
                  <Button
                    variant="contained"
                    color="primary"
                    className="google-login-button"
                    onClick={renderProps.onClick}
                    disabled={renderProps.disabled}
                    startIcon={<img src={GoogleLogo} alt="Google logo" className="google-logo" />}
                    sx={{ py: 1.5, px: 4, fontSize: '1.1rem', minWidth: '200px' }}
                  >
                    Get Started Free
                  </Button>
                )}
              />
              <Typography variant="body2" sx={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                fontSize: '0.875rem',
                textAlign: 'center'
              }}>
                No credit card required • Free trial available • Setup in minutes
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </div>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {loading ? (
            <Container sx={{ textAlign: 'center', mt: 10, flexGrow: 1 }}>
              <CircularProgress />
              <Typography variant="body1" mt={2}>Checking session…</Typography>
            </Container>
          ) : (
            <>
              <Header user={user} handleLogout={handleLogout} />
              <Box sx={{ flexGrow: 1 }}>
                <Routes>
                  {/* Default route for landing page */}
                  <Route
                    path="/"
                    element={
                      !token || !user ? (
                        renderLandingPage()
                      ) : (
                        <Dashboard token={token} user={user} handleLogout={handleLogout} />
                      )
                    }
                  />
                  {/* Dashboard route */}
                  <Route
                    path="/dashboard"
                    element={
                      token && user ? (
                        <Dashboard token={token} user={user} handleLogout={handleLogout} />
                      ) : (
                        renderLandingPage()
                      )
                    }
                  />
                  {/* GSC Chat route */}
                  <Route
                    path="/gsc"
                    element={
                      token && user ? (
                        <GSCChatInterface 
                          token={token} 
                          handleError={(err, retry) => {
                            setSnackbarMessage(err.message || 'An error occurred.');
                            setSnackbarOpen(true);
                            if (retry) retry();
                          }} 
                        />
                      ) : (
                        renderLandingPage()
                      )
                    }
                  />
                  {/* Privacy Policy route */}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  {/* Terms of Service route */}
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  {/* Success route for Stripe */}
                  <Route path="/success" element={<SuccessPage />} />
                  {/* Cancel route for Stripe */}
                  <Route path="/cancel" element={<CancelPage />} />
                  <Route
                    path="/tf"
                    element={
                      token && user ? (
                        <TfPage token={token} />
                      ) : (
                        renderLandingPage()
                      )
                    }
                  />
                </Routes>
              </Box>
              <Footer />
            </>
          )}
          <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
            <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default function AppWrapper() {
  if (!CLIENT_ID) {
    console.error('REACT_APP_GOOGLE_CLIENT_ID is not set');
    return <div>Error: Google Client ID not configured</div>;
  }
  return (
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <App />
    </GoogleOAuthProvider>
  );
}